import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getIntegrationsStatus } from './integrationService';
import { getAccountIntegrations } from './firestoreService';

/**
 * QuickBooks API Service
 * Uses Firebase callable functions to fetch QuickBooks data
 * Requires QuickBooks integration to be connected
 */

/**
 * Check if QuickBooks is connected (internal `dotware` doc and/or portal account doc).
 * @param {string|null|undefined} accountId - Company/account id from useCompanyId (portal)
 */
export const isQuickBooksConnected = async (accountId = null) => {
  const status = await getIntegrationsStatus({ forceRefresh: true });
  if (status?.quickbooks?.connected === true) return true;
  if (accountId) {
    try {
      const data = await getAccountIntegrations(accountId, { forceRefresh: true });
      const qb = data?.connectedIntegrations?.quickbooks;
      return qb?.connected === true;
    } catch (e) {
      console.warn('isQuickBooksConnected account check failed:', e);
      return false;
    }
  }
  return false;
};

/**
 * Get QuickBooks Profit & Loss report
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {{ accountId?: string }} [options]
 */
export const getProfitAndLossReport = async (startDate = null, endDate = null, options = {}) => {
  const getReport = httpsCallable(functions, 'getQuickBooksReport');
  const result = await getReport({
    reportType: 'ProfitAndLoss',
    startDate: startDate || `${new Date().getFullYear()}-01-01`,
    endDate: endDate || new Date().toISOString().split('T')[0],
    ...(options.summarizeColumnBy ? { summarizeColumnBy: options.summarizeColumnBy } : {}),
    ...(options.accountId ? { accountId: options.accountId } : {})
  });
  if (!result.data?.success) {
    throw new Error(result.data?.error || 'Failed to fetch Profit & Loss report');
  }
  return result.data.data;
};

/**
 * Get QuickBooks Balance Sheet report
 * @param {string} date - As-of date YYYY-MM-DD
 * @param {{ accountId?: string }} [options]
 */
export const getBalanceSheetReport = async (date = null, options = {}) => {
  const getReport = httpsCallable(functions, 'getQuickBooksReport');
  const result = await getReport({
    reportType: 'BalanceSheet',
    date: date || new Date().toISOString().split('T')[0],
    ...(options.accountId ? { accountId: options.accountId } : {})
  });
  if (!result.data?.success) {
    throw new Error(result.data?.error || 'Failed to fetch Balance Sheet report');
  }
  return result.data.data;
};

/**
 * Query QuickBooks entities (e.g., Purchase for expenses, Payment for revenue)
 * @param {string} query - QuickBooks query (e.g., "SELECT * FROM Purchase MAXRESULTS 1000")
 */
export const queryQuickBooks = async (query) => {
  const queryFn = httpsCallable(functions, 'queryQuickBooks');
  const result = await queryFn({ query });
  if (!result.data?.success) {
    throw new Error(result.data?.error || 'Failed to query QuickBooks');
  }
  return result.data.data;
};

/**
 * Get QuickBooks expenses (Purchase entities)
 */
export const getQuickBooksExpenses = async () => {
  const data = await queryQuickBooks('SELECT * FROM Purchase MAXRESULTS 1000');
  return data?.Purchase || [];
};

/**
 * Get QuickBooks payments (revenue - payments received)
 */
export const getQuickBooksPayments = async () => {
  const data = await queryQuickBooks('SELECT * FROM Payment MAXRESULTS 1000');
  return data?.Payment || [];
};

// --- Parsing helpers for QuickBooks reports (Rows.Row with group or Summary.ColData) ---

const getRowAmount = (row) => {
  const colData = row?.Summary?.ColData || row?.ColData;
  if (!colData) return null;
  const arr = Array.isArray(colData) ? colData : [colData];
  const amt = arr.find((c) => c?.value && !isNaN(parseFloat(String(c.value).replace(/[,$]/g, ''))));
  return amt?.value != null ? parseFloat(String(amt.value).replace(/[,$]/g, '')) : null;
};

const findRow = (rows, groups, labels) => {
  const rowList = Array.isArray(rows) ? rows : rows ? [rows] : [];
  return rowList.find(
    (r) =>
      (Array.isArray(groups) && groups.includes(r?.group)) ||
      (labels && (r?.Summary?.ColData || r?.ColData) && [].concat(r.Summary?.ColData || r.ColData).some((c) => labels.some((l) => (c?.value || '').toLowerCase().includes(l.toLowerCase()))))
  );
};

/**
 * Parse P&L report into key metrics.
 *
 * Every row lookup records whether the row was actually found so consumers can
 * distinguish a true $0 from "QuickBooks chart of accounts doesn't match the
 * expected P&L sections" (which previously parsed as a silent 0 and skewed
 * GPM/NPM/overhead).
 *
 * @param {Object} report - QuickBooks ProfitAndLoss report response
 * @returns {{ revenue: number, cogs: number, grossProfit: number, totalExpenses: number, netIncome: number, gpm: number, npm: number, overheadPercent: number, diagnostics: Object }}
 */
export const parseProfitAndLossReport = (report) => {
  if (!report?.Rows?.Row) return null;
  const rows = Array.isArray(report.Rows.Row) ? report.Rows.Row : [report.Rows.Row];

  const revenueAmt = getRowAmount(findRow(rows, ['TotalIncome'], ['total income', 'income (total)']));
  const cogsAmt = getRowAmount(findRow(rows, ['CostOfGoodsSold', 'TotalCostOfGoodsSold'], ['cost of goods sold', 'total cost of goods sold', 'cogs']));
  const expensesAmt = getRowAmount(findRow(rows, ['TotalExpenses'], ['total expenses', 'expenses (total)']));
  // Prefer the true bottom line (NetIncome) over NetOperatingIncome — they
  // differ whenever Other Income/Expenses exist.
  const netIncomeAmt = getRowAmount(findRow(rows, ['NetIncome'], null))
    ?? getRowAmount(findRow(rows, ['NetOperatingIncome'], ['net income', 'net operating income']));
  // Prefer QB's own Gross Profit row when present.
  const grossProfitAmt = getRowAmount(findRow(rows, ['GrossProfit'], ['gross profit']));

  const rowsFound = {
    revenue: revenueAmt != null,
    cogs: cogsAmt != null,
    totalExpenses: expensesAmt != null,
    netIncome: netIncomeAmt != null,
    grossProfit: grossProfitAmt != null
  };
  const warnings = [];
  if (!rowsFound.revenue) warnings.push('Revenue (Total Income) row not found in the P&L — revenue is unavailable. Check the chart of accounts uses Income-type accounts.');
  if (!rowsFound.cogs && !rowsFound.grossProfit) warnings.push('Cost of Goods Sold section not found in the P&L — GPM is unavailable (would otherwise be overstated by assuming $0 COGS).');
  if (!rowsFound.totalExpenses) warnings.push('Total Expenses row not found in the P&L — overhead % is unavailable.');
  if (!rowsFound.netIncome) warnings.push('Net Income row not found in the P&L — NPM is unavailable.');

  const revenue = revenueAmt;
  const cogs = cogsAmt;
  const totalExpenses = expensesAmt;
  const netIncome = netIncomeAmt;
  const grossProfit = grossProfitAmt ?? (revenue != null && cogs != null ? revenue - cogs : null);
  const gpm = revenue > 0 && grossProfit != null ? (grossProfit / revenue) * 100 : null;
  const npm = revenue > 0 && netIncome != null ? (netIncome / revenue) * 100 : null;
  const overheadPercent = revenue > 0 && totalExpenses != null ? (totalExpenses / revenue) * 100 : null;

  // Record which section groups QB actually returned — dev mode uses this to
  // show why a row didn't match.
  const rowGroups = rows.map((r) => r?.group).filter(Boolean);

  return {
    revenue,
    cogs,
    grossProfit,
    totalExpenses,
    netIncome,
    gpm,
    npm,
    overheadPercent,
    diagnostics: {
      rowsFound,
      warnings,
      rowGroups,
      // QB report header carries the actual period and accounting basis (Cash vs Accrual)
      reportPeriod: report?.Header
        ? {
            startPeriod: report.Header.StartPeriod || null,
            endPeriod: report.Header.EndPeriod || null,
            basis: report.Header.ReportBasis || null
          }
        : null
    }
  };
};

// Full ColData array for a report row (summary rows carry Summary.ColData).
const getRowColData = (row) => {
  const colData = row?.Summary?.ColData || row?.ColData;
  if (!colData) return null;
  return Array.isArray(colData) ? colData : [colData];
};

// Numeric value at a given column index within a row's ColData.
const colNum = (colData, idx) => {
  const v = colData?.[idx]?.value;
  if (v == null || v === '') return null;
  const n = parseFloat(String(v).replace(/[,$]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Parse a P&L summarized by month (summarize_column_by=Month) into a per-month
 * array of income-statement figures + margins. QuickBooks returns one column per
 * month (plus a leading label column and a trailing "Total"); each summary row's
 * ColData aligns to those columns.
 *
 * @param {Object} report - ProfitAndLoss report response with monthly columns
 * @returns {{ months: Array<{year:number, monthIndex:number, revenue:number|null, cogs:number|null, grossProfit:number|null, totalExpenses:number|null, netIncome:number|null, npm:number|null, gpm:number|null, overheadPercent:number|null}>, diagnostics: Object }|null}
 */
export const parseMonthlyProfitAndLoss = (report) => {
  const columns = report?.Columns?.Column;
  if (!report?.Rows?.Row || !columns) return null;
  const colList = Array.isArray(columns) ? columns : [columns];
  const rows = Array.isArray(report.Rows.Row) ? report.Rows.Row : [report.Rows.Row];

  // Column index → month. Prefer MetaData StartDate; fall back to the column
  // title ("Jan 2026"). Label/Total columns carry no month and are skipped.
  const monthCols = [];
  colList.forEach((col, idx) => {
    const metaArr = Array.isArray(col?.MetaData) ? col.MetaData : col?.MetaData ? [col.MetaData] : [];
    const startDate = metaArr.find((m) => m?.Name === 'StartDate')?.Value;
    let year = null;
    let monthIndex = null;
    if (startDate && /^\d{4}-\d{2}-\d{2}/.test(startDate)) {
      const d = new Date(`${startDate}T12:00:00`);
      year = d.getFullYear();
      monthIndex = d.getMonth();
    } else {
      const m = (col?.ColTitle || '').match(/([A-Za-z]{3,})\s+(\d{4})/);
      if (m) {
        const mi = MONTH_ABBR.findIndex((s) => m[1].toLowerCase().startsWith(s.toLowerCase()));
        if (mi !== -1) {
          monthIndex = mi;
          year = parseInt(m[2], 10);
        }
      }
    }
    if (year != null && monthIndex != null) monthCols.push({ idx, year, monthIndex });
  });

  const incomeRow = findRow(rows, ['TotalIncome'], ['total income', 'income (total)']);
  const cogsRow = findRow(rows, ['CostOfGoodsSold', 'TotalCostOfGoodsSold'], ['cost of goods sold', 'total cost of goods sold', 'cogs']);
  const expensesRow = findRow(rows, ['TotalExpenses'], ['total expenses', 'expenses (total)']);
  const grossProfitRow = findRow(rows, ['GrossProfit'], ['gross profit']);
  const netIncomeRow = findRow(rows, ['NetIncome'], null) || findRow(rows, ['NetOperatingIncome'], ['net income', 'net operating income']);

  const incomeCols = getRowColData(incomeRow);
  const cogsCols = getRowColData(cogsRow);
  const expensesCols = getRowColData(expensesRow);
  const grossProfitCols = getRowColData(grossProfitRow);
  const netIncomeCols = getRowColData(netIncomeRow);

  const months = monthCols.map(({ idx, year, monthIndex }) => {
    const revenue = colNum(incomeCols, idx);
    const cogs = colNum(cogsCols, idx);
    const totalExpenses = colNum(expensesCols, idx);
    const netIncome = colNum(netIncomeCols, idx);
    const grossProfit = colNum(grossProfitCols, idx) ?? (revenue != null && cogs != null ? revenue - cogs : null);
    const gpm = revenue > 0 && grossProfit != null ? (grossProfit / revenue) * 100 : null;
    const npm = revenue > 0 && netIncome != null ? (netIncome / revenue) * 100 : null;
    const overheadPercent = revenue > 0 && totalExpenses != null ? (totalExpenses / revenue) * 100 : null;
    return { year, monthIndex, revenue, cogs, grossProfit, totalExpenses, netIncome, npm, gpm, overheadPercent };
  });

  const warnings = [];
  if (monthCols.length === 0) warnings.push('No monthly columns found in the P&L — the report was not summarized by month.');
  if (!incomeRow) warnings.push('Revenue (Total Income) row not found in the monthly P&L.');
  if (!netIncomeRow) warnings.push('Net Income row not found in the monthly P&L — monthly NPM is unavailable.');

  return {
    months,
    diagnostics: {
      monthColumnCount: monthCols.length,
      rowsFound: { revenue: !!incomeRow, cogs: !!cogsRow, totalExpenses: !!expensesRow, grossProfit: !!grossProfitRow, netIncome: !!netIncomeRow },
      warnings,
      reportPeriod: report?.Header
        ? { startPeriod: report.Header.StartPeriod || null, endPeriod: report.Header.EndPeriod || null, basis: report.Header.ReportBasis || null }
        : null
    }
  };
};

/**
 * Fetch and parse a monthly-columned P&L for a date range in a single API call.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {{ accountId?: string }} [options]
 */
export const getMonthlyProfitAndLoss = async (startDate, endDate, options = {}) => {
  const report = await getProfitAndLossReport(startDate, endDate, { ...options, summarizeColumnBy: 'Month' });
  return parseMonthlyProfitAndLoss(report);
};

/**
 * Parse Balance Sheet report into key metrics
 * @param {Object} report - QuickBooks Balance Sheet report response
 * @returns {{ totalAssets: number, totalLiabilities: number, totalEquity: number, cash: number }}
 */
export const parseBalanceSheetReport = (report) => {
  if (!report?.Rows?.Row) return null;
  const rows = Array.isArray(report.Rows.Row) ? report.Rows.Row : [report.Rows.Row];
  const totalAssetsAmt = getRowAmount(findRow(rows, ['TotalAssets'], ['total assets']));
  const totalLiabilitiesAmt = getRowAmount(findRow(rows, ['TotalLiabilities'], ['total liabilities']));
  const totalEquityAmt = getRowAmount(findRow(rows, ['TotalEquity'], ['total equity']));

  const rowLabel = (r) =>
    (r?.Summary?.ColData?.[0]?.value || r?.ColData?.[0]?.value || '').toLowerCase();

  // Cash: prefer the "Bank Accounts" section total; otherwise sum leaf rows whose
  // label looks like a cash account. Never fall back to total assets — that
  // wildly overstated cash (and runway) for companies with WIP/fixed assets.
  let cash = null;
  let cashMethod = null;
  const findBankAccountsSection = (rowList) => {
    for (const row of rowList) {
      if (row?.group === 'BankAccounts' || rowLabel(row).includes('total bank accounts')) return row;
      if (row?.Rows?.Row) {
        const subRows = Array.isArray(row.Rows.Row) ? row.Rows.Row : [row.Rows.Row];
        const found = findBankAccountsSection(subRows);
        if (found) return found;
      }
    }
    return null;
  };
  const bankSection = findBankAccountsSection(rows);
  if (bankSection) {
    const amt = getRowAmount(bankSection);
    if (amt != null) {
      cash = amt;
      cashMethod = 'bank-accounts-section';
    }
  }
  if (cash == null) {
    // Sum leaf rows (no nested Rows) that look like cash accounts, avoiding
    // double counting section summaries.
    const CASH_LABELS = ['cash', 'checking', 'savings'];
    let sum = 0;
    let found = false;
    const walk = (rowList) => {
      for (const row of rowList) {
        if (row?.Rows?.Row) {
          walk(Array.isArray(row.Rows.Row) ? row.Rows.Row : [row.Rows.Row]);
        } else {
          const label = rowLabel(row);
          if (label && !label.startsWith('total') && CASH_LABELS.some((l) => label.includes(l))) {
            const amt = getRowAmount(row);
            if (amt != null) {
              sum += amt;
              found = true;
            }
          }
        }
      }
    };
    walk(rows);
    if (found) {
      cash = sum;
      cashMethod = 'cash-labeled-rows';
    }
  }

  const rowsFound = {
    totalAssets: totalAssetsAmt != null,
    totalLiabilities: totalLiabilitiesAmt != null,
    totalEquity: totalEquityAmt != null,
    cash: cash != null
  };
  const warnings = [];
  if (!rowsFound.totalAssets) warnings.push('Total Assets row not found on the balance sheet.');
  if (!rowsFound.totalLiabilities) warnings.push('Total Liabilities row not found on the balance sheet — debt reads as $0.');
  if (!rowsFound.totalEquity) warnings.push('Total Equity row not found — debt-to-equity cannot be computed.');
  if (!rowsFound.cash) warnings.push('No bank/cash accounts found on the balance sheet — cash and runway are unavailable (no longer approximated by total assets).');

  return {
    totalAssets: totalAssetsAmt ?? 0,
    totalLiabilities: totalLiabilitiesAmt ?? 0,
    totalEquity: totalEquityAmt ?? 0,
    cash,
    diagnostics: {
      rowsFound,
      warnings,
      cashMethod,
      reportPeriod: report?.Header
        ? { startPeriod: report.Header.StartPeriod || null, endPeriod: report.Header.EndPeriod || null, basis: report.Header.ReportBasis || null }
        : null
    }
  };
};

/**
 * Get revenue by month for a given year from QuickBooks P&L (one P&L per month)
 * @param {number} year
 * @returns {Promise<Array<{ month: string, monthIndex: number, year: number, value: number }>>}
 */
export const getRevenueByMonth = async (year) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const result = [];
  for (let m = 0; m < 12; m++) {
    const start = new Date(year, m, 1);
    const end = new Date(year, m + 1, 0);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    try {
      const report = await getProfitAndLossReport(startStr, endStr);
      const parsed = parseProfitAndLossReport(report);
      result.push({
        month: monthNames[m],
        monthIndex: m + 1,
        year,
        value: parsed?.revenue ?? 0
      });
    } catch (e) {
      result.push({ month: monthNames[m], monthIndex: m + 1, year, value: 0 });
    }
  }
  return result;
};

/**
 * Get cached QuickBooks revenue history (monthly entries)
 * @param {{ autoSyncIfEmpty?: boolean, lookbackYears?: number }} options
 */
export const getQuickBooksRevenueHistory = async (options = {}) => {
  const fn = httpsCallable(functions, 'getQuickBooksRevenueHistory');
  const result = await fn({
    autoSyncIfEmpty: options.autoSyncIfEmpty ?? true,
    lookbackYears: options.lookbackYears ?? 8,
    ...(options.accountId ? { accountId: options.accountId } : {})
  });
  if (!result.data?.success) {
    throw new Error(result.data?.error || 'Failed to fetch QuickBooks revenue history');
  }
  return result.data.data || { revenueByMonth: [], metadata: {} };
};

/**
 * Sync QuickBooks revenue cache for historical and/or a specific month
 * @param {{ lookbackYears?: number, syncOnlyMonth?: {year:number, monthIndex:number}|null, debug?: boolean }} options
 */
export const syncQuickBooksRevenue = async (options = {}) => {
  const fn = httpsCallable(functions, 'syncQuickBooksRevenue');
  const result = await fn({
    lookbackYears: options.lookbackYears ?? 8,
    syncOnlyMonth: options.syncOnlyMonth || null,
    debug: options.debug === true,
    ...(options.accountId ? { accountId: options.accountId } : {})
  });
  if (!result.data?.success) {
    throw new Error(result.data?.error || 'Failed to sync QuickBooks revenue');
  }
  return result.data;
};

/**
 * Sum the most recent 12 months of QuickBooks P&L revenue (trailing twelve months).
 * @param {Array<{ year: number, monthIndex: number, value?: number }>|null|undefined} revenueByMonth
 * @returns {number|null}
 */
export function computeTrailingTwelveMonthRevenue(revenueByMonth) {
  if (!Array.isArray(revenueByMonth) || revenueByMonth.length === 0) return null;
  const sorted = [...revenueByMonth]
    .filter((m) => m?.year && m?.monthIndex)
    .sort((a, b) => (a.year - b.year) || (a.monthIndex - b.monthIndex));
  if (sorted.length === 0) return null;
  const recent = sorted.slice(-12);
  const total = recent.reduce((sum, m) => sum + Number(m.value || 0), 0);
  return Number.isFinite(total) ? Math.round(total) : null;
}

/**
 * Sum QuickBooks revenue for the current calendar year (YTD).
 * @param {Array<{ year: number, monthIndex: number, value?: number }>|null|undefined} revenueByMonth
 * @returns {number|null}
 */
export function computeCurrentYearRevenue(revenueByMonth) {
  if (!Array.isArray(revenueByMonth) || revenueByMonth.length === 0) return null;
  const year = new Date().getFullYear();
  const total = revenueByMonth
    .filter((m) => m?.year === year)
    .reduce((sum, m) => sum + Number(m.value || 0), 0);
  return Number.isFinite(total) ? Math.round(total) : null;
}

/**
 * Live YTD total income from QuickBooks P&L for an account.
 * @param {string} accountId
 * @returns {Promise<number|null>}
 */
export const getQuickBooksYtdRevenue = async (accountId) => {
  const year = new Date().getFullYear();
  const report = await getProfitAndLossReport(`${year}-01-01`, new Date().toISOString().split('T')[0], { accountId });
  const parsed = parseProfitAndLossReport(report);
  if (!parsed || parsed.revenue == null) return null;
  return Math.round(parsed.revenue);
};

/**
 * Get NPM (net profit margin %) by month for a given year
 * @param {number} year
 * @returns {Promise<Array<{ month: string, monthIndex: number, year: number, value: number }>>}
 */
export const getNpmByMonth = async (year, options = {}) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const result = [];
  for (let m = 0; m < 12; m++) {
    const start = new Date(year, m, 1);
    const end = new Date(year, m + 1, 0);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    try {
      const report = await getProfitAndLossReport(startStr, endStr, options);
      const parsed = parseProfitAndLossReport(report);
      result.push({
        month: monthNames[m],
        monthIndex: m + 1,
        year,
        value: parsed?.npm ?? 0
      });
    } catch (e) {
      result.push({ month: monthNames[m], monthIndex: m + 1, year, value: 0 });
    }
  }
  return result;
};

/**
 * Get GPM (gross profit margin %) by month for a given year
 * @param {number} year
 * @returns {Promise<Array<{ month: string, monthIndex: number, year: number, value: number }>>}
 */
export const getGpmByMonth = async (year, options = {}) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const result = [];
  for (let m = 0; m < 12; m++) {
    const start = new Date(year, m, 1);
    const end = new Date(year, m + 1, 0);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    try {
      const report = await getProfitAndLossReport(startStr, endStr, options);
      const parsed = parseProfitAndLossReport(report);
      result.push({
        month: monthNames[m],
        monthIndex: m + 1,
        year,
        value: parsed?.gpm ?? 0
      });
    } catch (e) {
      result.push({ month: monthNames[m], monthIndex: m + 1, year, value: 0 });
    }
  }
  return result;
};

/**
 * Get NPM, GPM, and overhead from P&L for a date range
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 */
export const getNpmGpmOverheadFromPandL = async (startDate = null, endDate = null, options = {}) => {
  const end = endDate || new Date().toISOString().split('T')[0];
  const start = startDate || `${new Date().getFullYear()}-01-01`;
  const report = await getProfitAndLossReport(start, end, options);
  return parseProfitAndLossReport(report);
};

/**
 * Get balance sheet liabilities and cash for a given date
 * @param {string} date - YYYY-MM-DD
 * @param {{ accountId?: string }} [options]
 */
export const getBalanceSheetLiabilities = async (date = null, options = {}) => {
  const report = await getBalanceSheetReport(date || new Date().toISOString().split('T')[0], options);
  return parseBalanceSheetReport(report);
};

/**
 * Get cash and runway (months of runway = cash / monthly burn)
 * @param {string} asOfDate - YYYY-MM-DD for balance sheet
 * @param {number} monthsForBurn - number of months to average for monthly burn (default 3)
 * @param {{ accountId?: string }} [options]
 */
export const getCashAndRunway = async (asOfDate = null, monthsForBurn = 3, options = {}) => {
  const todayStr = asOfDate || new Date().toISOString().split('T')[0];
  const balanceSheet = await getBalanceSheetReport(todayStr, options);
  const parsed = parseBalanceSheetReport(balanceSheet);
  const cashFound = parsed?.cash != null;
  const cash = parsed?.cash ?? 0;
  const end = new Date(todayStr + 'T12:00:00');
  const start = new Date(todayStr + 'T12:00:00');
  start.setMonth(start.getMonth() - monthsForBurn);
  const startStr = start.toISOString().split('T')[0];
  const endStr = end.toISOString().split('T')[0];
  let monthlyBurn = 0;
  let burnInputs = null;
  let plWarnings = [];
  try {
    const pl = await getProfitAndLossReport(startStr, endStr, options);
    const plParsed = parseProfitAndLossReport(pl);
    const totalExpenses = (plParsed?.totalExpenses ?? 0) + (plParsed?.cogs ?? 0);
    monthlyBurn = monthsForBurn > 0 ? totalExpenses / monthsForBurn : 0;
    burnInputs = {
      totalExpenses: plParsed?.totalExpenses ?? 0,
      cogs: plParsed?.cogs ?? 0,
      periodStart: startStr,
      periodEnd: endStr,
      monthsForBurn
    };
    plWarnings = plParsed?.diagnostics?.warnings || [];
  } catch (_) {}
  const runwayMonths = cashFound && monthlyBurn > 0 ? cash / monthlyBurn : (cashFound && cash > 0 ? 999 : 0);
  return {
    cash: cashFound ? cash : null,
    monthlyBurn,
    runwayMonths: Math.round(runwayMonths * 10) / 10,
    diagnostics: {
      cashFound,
      cashMethod: parsed?.diagnostics?.cashMethod || null,
      balanceSheetWarnings: parsed?.diagnostics?.warnings || [],
      plWarnings,
      burnInputs
    }
  };
};
